import { useEffect, useState, useCallback } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, CalendarDays } from "lucide-react";
import ApplianceDailyEmailCard from "@/components/ApplianceDailyEmailCard";

export default function ApplianceReport() {
  const [date, setDate] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const params = d ? { date: d } : {};
      const { data } = await api.get("/appliance/report/preview", { params });
      setReport(data);
      if (!d && data.date) setDate(data.date);
    } catch {
      toast.error("Could not load report preview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = report?.totals;

  return (
    <div className="p-6 lg:p-10 max-w-[1100px] animate-fade-in" data-testid="appliance-report-page">
      <div className="mb-6">
        <div className="label-eyebrow">Appliance Depot</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Daily report</h1>
        <p className="text-muted-foreground mt-1">Preview of the daily documents summary.</p>
      </div>

      {/* Email subscription */}
      <div className="mb-6">
        <ApplianceDailyEmailCard />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative">
          <CalendarDays className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="pl-9 w-[190px]"
            data-testid="appliance-report-date"
          />
        </div>
        <Button
          onClick={() => load(date)}
          disabled={loading}
          className="bg-primary text-primary-foreground"
          data-testid="appliance-report-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Update preview
        </Button>
      </div>

      {/* Summary tiles */}
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="stat-card" data-testid="appliance-report-kpi-orders">
            <div className="label-eyebrow">Orders (revenue)</div>
            <div className="num font-heading text-2xl font-bold text-[#0f766e] mt-2">{fmtCurrency(t.sales_order_total)}</div>
            <div className="text-xs text-muted-foreground mt-1">{t.sales_order_count} confirmed</div>
          </div>
          <div className="stat-card" data-testid="appliance-report-kpi-quotes">
            <div className="label-eyebrow">Quotes (pipeline)</div>
            <div className="num font-heading text-2xl font-bold text-[#1d4ed8] mt-2">{fmtCurrency(t.invoice_total)}</div>
            <div className="text-xs text-muted-foreground mt-1">{t.invoice_count} quotes</div>
          </div>
          <div className="stat-card" data-testid="appliance-report-kpi-delivery">
            <div className="label-eyebrow">Delivery</div>
            <div className="num font-heading text-2xl font-bold text-[#b45309] mt-2">{fmtCurrency(t.delivery_costs)}</div>
          </div>
          <div className="stat-card" data-testid="appliance-report-kpi-documents">
            <div className="label-eyebrow">Documents</div>
            <div className="num font-heading text-2xl font-bold mt-2">{t.document_count}</div>
            <div className="text-xs text-muted-foreground mt-1">{t.sales_order_count} orders · {t.invoice_count} quotes</div>
          </div>
        </div>
      )}

      {/* Email HTML preview */}
      <div className="surface-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border/70 text-sm font-medium text-muted-foreground">
          Email preview {report?.label ? `· ${report.label}` : ""}
        </div>
        {report ? (
          <iframe
            title="Appliance daily report preview"
            srcDoc={report.html}
            className="w-full"
            style={{ height: 640, border: 0, background: "#eef2f7" }}
            data-testid="appliance-report-iframe"
          />
        ) : (
          <div className="py-16 text-center text-muted-foreground text-sm">Loading preview…</div>
        )}
      </div>
    </div>
  );
}
